<template>
  <Portal>
    <ModalComponent :show="true" v-on="$listeners">
      <Card theme="flat" class="shop-contextual-images-modal">
        <template #default>
          <header>
            <h2>{{ $tc('contextualImage', 0) }}</h2>

            <ButtonComponent icon="fa-pro-light:times" @click="$emit('close')" />
          </header>

          <div class="shop-contextual-images-modal__items" :empty-label="$tc('xActiveContextualImages', 0)">
            <div
              v-for="(item, index) in data"
              class="shop-contextual-images-modal__item"
              :class="{ 'shop-contextual-images-modal__item--error': !isValid(item) }"
            >
              <FontAwesomeIcon v-if="!isValid(item)" icon="fa-pro-light:exclamation-triangle" />
              <img v-else :src="item.images[0].url" :alt="item.images[0].name || `${$tc('image', 1)} ${index}`" />

              <div
                :class="[
                  'shop-contextual-images-modal__item-label',
                  { 'shop-contextual-images-modal__item__label--missing': !getLabel(item) },
                ]"
              >
                {{ getLabel(item) || $t('missingInformation') }}
              </div>

              <div class="shop-contextual-images-modal__item-actions">
                <ButtonComponent
                  theme="secondary"
                  icon="fa-pro-light:pen"
                  @click="() => openDialog({ index, ...item })"
                />
                <ButtonComponent theme="error" icon="fa-pro-light:trash" @click="() => remove(index)" />
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <ButtonComponent
            v-if="data.length > 1"
            :label="`${$t('delete')} ${$t('all', { x: null }).toLowerCase()}`"
            icon="fa-pro-light:trash"
            theme="error"
            confirm
            @click="removeAll"
          />
          <ButtonComponent
            :label="$t('create')"
            theme="secondary"
            icon="fa-pro-light:plus-circle"
            @click="() => openDialog()"
          />

          <ButtonComponent :label="$t('cancel')" icon="fa-pro-light:times" class="ml-auto" @click="$emit('close')" />
          <ButtonComponent :label="$t('confirm')" theme="primary" icon="fa-pro-light:angle-right" @click="submit" />
        </template>
      </Card>
    </ModalComponent>
    <SelectModal v-if="contextualImage" :value="contextualImage" @close="contextualImage = null" @input="update" />
  </Portal>
</template>

<script>
import Card from '@/components/Card'
import { ModalComponent } from 'vue-elder-modal'
import SelectModal, { getItemData } from './SelectModal'
import Portal from '@/components/Portal'
import { Clone } from '@/utils'
import Popover from '@/components/Popover'
import { isValid } from './utils'

export default {
  props: {
    value: Array,
  },
  inject: ['getItems'],
  watch: {
    value: {
      handler(val = []) {
        this.data = Clone(val).map(item => {
          if (!this.getItems) return item
          const itemIds = this.getItems().map(({ id }) => id)
          const clean = (arr = []) => arr.filter(i => itemIds.includes(i))
          item.appendTo = clean(item.appendTo)
          item.conditions = clean(item.appendTo)
          return item
        })

        if (!this.data.length) return this.openDialog()
      },
      immediate: true,
    },
  },
  data() {
    return {
      contextualImage: null,
      data: null,
    }
  },
  methods: {
    isValid(item) {
      return isValid(item, this.getItems())
    },
    update(data = {}) {
      const { index, ...rest } = data

      if (!index && index !== 0) {
        this.data.push(rest)
        return
      }
      this.data = this.data.toSpliced(index, 1, rest)
    },
    remove(index) {
      this.data = this.data.toSpliced(index, 1)
    },
    removeAll() {
      this.data = []
    },
    openDialog(value) {
      this.contextualImage = value || {
        displayName: '',
        appendTo: [],
        conditions: [],
        images: [],
      }
    },
    submit() {
      this.$emit('input', this.data)
      this.$emit('close')
    },
    getLabel(item) {
      return (
        item.displayName ||
        getItemData(item.conditions, this.getItems())
          .map(({ name }) => name)
          .join(', ')
      )
    },
  },
  components: {
    ModalComponent,
    Card,
    SelectModal,
    Portal,
    Popover,
  },
}
</script>

<style lang="scss">
.shop-contextual-images-modal {
  width: 100%;
  min-height: 600px;
  max-width: 950px;

  .card {
    &__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__footer {
      .end {
        display: flex;
        justify-content: space-between;
        width: 100%;

        section {
          display: flex;
          gap: 1rem;
        }
      }
    }
  }

  header {
    display: flex;
    gap: 1rem;

    h2 {
      line-height: 1;
      margin: 0 auto 0 0;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 500px;
    overflow-y: auto;
    border-radius: var(--border-radius);

    flex-grow: 1;

    &:empty {
      background-color: rgba(black, 0.03);

      &:before {
        content: attr(empty-label);
        opacity: 0.6;
        margin: auto;
      }
    }
  }

  &__item {
    --spacing: 1rem;

    grid-column: 1 / -1;

    display: grid;
    grid-template-columns: 4rem 1fr auto;
    align-items: center;
    gap: var(--spacing);

    flex-shrink: 0;

    overflow: clip;

    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);

    &--error {
      border-color: hsl(var(--error-h), var(--error-s), 80%);

      > svg {
        margin-block: 1.1rem;
        color: var(--error);
        width: 100%;
        aspect-ratio: 1;
        font-size: 1.5rem;
      }
    }

    &-actions {
      display: flex;
      gap: calc(var(--spacing) / 2);

      padding-right: var(--spacing);

      .elder-button {
        $size: 2.25rem;

        border: none;

        width: $size;
        height: $size;
        border-radius: 100%;
      }
    }

    img {
      width: 100%;
      aspect-ratio: 1;

      background-color: var(--border-color);
    }
  }

  .elder-button {
    &--error {
      background-color: #9c23230d !important;
      border-color: #9c2323 !important;
      color: #9c2323 !important;

      &:hover {
        background-color: #9c2323 !important;
        color: white !important;
      }
    }
  }
}
</style>
